import { isSouthernAfrica } from '@/contexts/RegionContext';

/**
 * List of known proper nouns, institutional names, and official trademarks that
 * must never undergo regional spelling substitution (e.g., 'World Health Organization'
 * must not become 'World Health Organisation' in the UK branch, nor should
 * 'Ministry of Defence' become 'Ministry of Defense' in the US branch).
 */
export const DEFAULT_PROTECTED_PROPER_NOUNS: readonly string[] = [
  // Global & international bodies
  'World Health Organization',
  'World Health Organization\'s',
  'World Trade Organization',
  'International Labour Organization',
  'International Organization for Standardization',
  'United Nations Educational, Scientific and Cultural Organization',
  
  // Centers of excellence, culture & institutions
  'Centers for Disease Control and Prevention',
  'Centers for Disease Control',
  'Center for Creative Leadership',
  'Center for Nonviolent Communication',
  'Center for Executive Coaching',
  'Center for Credentialing & Education',
  'Rockefeller Center',
  'Lincoln Center',
  'Kennedy Center',
  'Barclays Center',
  
  // Defense & Government
  'Department of Defense',
  'National Defense University',
  'Ministry of Defence',
  'Secretary of Defense',
  'Secretary of State for Defence',
  
  // Assessment & Framework Brand Names
  'FIVE LENS',
  'FIVE LENS Enneagram'
];

/**
 * Symmetrical word substitution pairs between UK/SA and US English.
 * Format: [UK/SA spelling, US spelling]
 */
export const SPELLING_PAIRS: ReadonlyArray<[uk: string, us: string]> = [
  // Multi-word exact phrases first (prevent sub-token fragmentation)
  ['Energy Centres', 'Energy Centers'],
  ['energy centres', 'energy centers'],

  // Organization / Organisation family
  ['organisation', 'organization'],
  ['organisations', 'organizations'],
  ['organisational', 'organizational'],
  ['organisationally', 'organizationally'],

  // Analyse / Analyze family
  ['analyse', 'analyze'],
  ['analyses', 'analyzes'],
  ['analysed', 'analyzed'],
  ['analysing', 'analyzing'],
  ['analyser', 'analyzer'],
  ['analysers', 'analyzers'],

  // Optimise / Optimize family
  ['optimise', 'optimize'],
  ['optimises', 'optimizes'],
  ['optimised', 'optimized'],
  ['optimising', 'optimizing'],
  ['optimisation', 'optimization'],
  ['optimisations', 'optimizations'],
  ['optimiser', 'optimizer'],
  ['optimisers', 'optimizers'],

  // Customise / Customize family
  ['customise', 'customize'],
  ['customises', 'customizes'],
  ['customised', 'customized'],
  ['customising', 'customizing'],
  ['customisation', 'customization'],
  ['customisations', 'customizations'],

  // Defence / Defense family
  ['defence', 'defense'],
  ['defences', 'defenses'],
  ['defenceless', 'defenseless'],

  // Centre / Center family
  ['centre', 'center'],
  ['centres', 'centers'],
  ['centred', 'centered'],
  ['centring', 'centering'],

  // Behaviour / Behavior family
  ['behaviour', 'behavior'],
  ['behaviours', 'behaviors'],
  ['behavioural', 'behavioral'],
  ['behaviourally', 'behaviorally'],
];

export interface ApplySpellingOptions {
  /**
   * Additional proper nouns or phrases to protect from substitution.
   */
  protectedProperNouns?: string[];
}

/**
 * Escapes regex special characters in a literal string.
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Replaces occurrences of a word or phrase while preserving matching case:
 * - UPPERCASE -> UPPERCASE
 * - Capitalized/Titlecase -> Capitalized/Titlecase
 * - lowercase -> lowercase
 */
function replaceWithCase(text: string, fromWord: string, toWord: string): string {
  const fromUpper = fromWord.toUpperCase();
  const toUpper = toWord.toUpperCase();

  const fromCap = fromWord.charAt(0).toUpperCase() + fromWord.slice(1);
  const toCap = toWord.charAt(0).toUpperCase() + toWord.slice(1);

  const fromLower = fromWord.toLowerCase();
  const toLower = toWord.toLowerCase();

  let res = text;
  if (fromWord.includes(' ')) {
    res = res.replace(new RegExp(escapeRegex(fromWord), 'g'), toWord);
    res = res.replace(new RegExp(escapeRegex(fromCap), 'g'), toCap);
    res = res.replace(new RegExp(escapeRegex(fromUpper), 'g'), toUpper);
  } else {
    // UPPERCASE first so it does not collide with Capitalized
    res = res.replace(new RegExp(`\\b${escapeRegex(fromUpper)}\\b`, 'g'), toUpper);
    res = res.replace(new RegExp(`\\b${escapeRegex(fromCap)}\\b`, 'g'), toCap);
    res = res.replace(new RegExp(`\\b${escapeRegex(fromLower)}\\b`, 'g'), toLower);
  }
  return res;
}

/**
 * Applies regional spelling rules (UK/SA vs US) to a string while:
 * 1. Protecting known proper nouns (e.g. World Health Organization) from substitution.
 * 2. Maintaining symmetrical, complete word lists for organisation, analyse, optimise, customise, defence, centre, and behaviour.
 * 3. Correctly identifying Southern African regions (za, na, bw, ls, sz) as using UK/SA spelling.
 */
export function applySpelling(
  text: string, 
  region: string, 
  options?: ApplySpellingOptions
): string {
  if (!text) return '';

  const cleanRegion = (region || '').toLowerCase().trim();
  const isUK = 
    ['gb', 'au', 'uk'].includes(cleanRegion) || 
    isSouthernAfrica(cleanRegion) || 
    cleanRegion.includes('za') || 
    cleanRegion.includes('gb');

  // Step 1: Tokenize and protect proper nouns
  const protectedList = [
    ...DEFAULT_PROTECTED_PROPER_NOUNS,
    ...(options?.protectedProperNouns || [])
  ].sort((a, b) => b.length - a.length); // Longer phrases first

  const tokens: string[] = [];
  let protectedText = text;

  for (const noun of protectedList) {
    if (!noun) continue;
    const regex = new RegExp(`\\b${escapeRegex(noun)}\\b`, 'g');
    protectedText = protectedText.replace(regex, (match) => {
      const token = `__PROTECTED_PROPER_NOUN_${tokens.length}__`;
      tokens.push(match);
      return token;
    });
  }

  // Step 2: Apply symmetrical spelling replacements
  let transformed = protectedText;
  for (const [ukWord, usWord] of SPELLING_PAIRS) {
    const fromWord = isUK ? usWord : ukWord;
    const toWord = isUK ? ukWord : usWord;
    transformed = replaceWithCase(transformed, fromWord, toWord);
  }

  // Step 3: Restore all protected proper nouns
  for (let i = 0; i < tokens.length; i++) {
    transformed = transformed.replace(`__PROTECTED_PROPER_NOUN_${i}__`, tokens[i]);
  }

  return transformed;
}
